import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Crown, Loader2, ShieldCheck, Sparkles, Star } from "lucide-react";
import { authHeader, getUserToken } from "@/utils/userAuth";
import { extractApiError } from "@/utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Premium "Coaching Completo" sales page (€490).                            */
/*  · Visible ONLY via admin-generated magic-link (/corso/auto-login?...).    */
/*  · Hidden from public navigation. Hidden from search engines (noindex).    */
/*  · 3 bundled items: 10h coaching + 4h coaching + corso completo,           */
/*    with a visible "before / after" discount anchor.                        */
/* -------------------------------------------------------------------------- */

const ITEMS = [
  {
    icon: Crown,
    name: "10 ore di consulenza 1-to-1",
    desc: "Sessioni profonde con Alessandro — il valore più alto del pacchetto.",
    priceLabel: "€490",
  },
  {
    icon: Star,
    name: "4 ore di consulenza 1-to-1",
    desc: "Pacchetto integrativo per follow-up mirati e verifiche del protocollo.",
    priceLabel: "€200",
  },
  {
    icon: Sparkles,
    name: "Corso Completo (9 lezioni)",
    desc: "Accesso permanente a tutto il percorso Figlio a Carico.",
    priceLabel: "€197",
  },
];

const SUM_FULL = 490 + 200 + 197; // €887
const SALE_PRICE = 490; // pagamento finale

function firstName(name) {
  return name ? String(name).trim().split(/\s+/)[0] : "";
}

export default function CoachingBundlePage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!getUserToken()) {
      navigate("/login?next=/vendita/coaching-completo", { replace: true });
      return;
    }
    axios.get(`${API}/users/me`, { headers: authHeader() })
      .then((r) => setMe(r.data))
      .catch(() => { /* user can still see page */ })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleCheckout = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/checkout/coaching-bundle`,
        {},
        { headers: authHeader() },
      );
      if (data?.url) {
        window.location.replace(data.url);
        return;
      }
      setError("Risposta inattesa dal server. Riprova.");
    } catch (err) {
      setError(extractApiError(err, "Errore nella creazione del carrello."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-primary)" }}>
        <Loader2 className="animate-spin" size={28} style={{ color: "var(--np-secondary)" }} />
      </main>
    );
  }

  const greetingName = firstName(me?.name);

  return (
    <main
      className="min-h-screen relative overflow-hidden"
      style={{ background: "var(--np-primary)", color: "#fff" }}
      data-testid="coaching-bundle-page"
    >
      {/* Decorative grain + radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% -5%, rgba(213,93,57,0.28), transparent 70%), radial-gradient(ellipse 35% 25% at 80% 90%, rgba(213,93,57,0.12), transparent 70%)",
        }}
      />

      <div className="relative mx-auto px-6" style={{ maxWidth: 880, paddingTop: "clamp(72px, 9vw, 110px)", paddingBottom: "clamp(80px, 11vw, 140px)" }}>
        {/* Accent line + premium badge */}
        <div className="flex justify-center mb-7">
          <div className="w-12 h-[2px]" style={{ background: "var(--np-secondary)" }} aria-hidden />
        </div>
        <div className="flex justify-center mb-7">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ background: "rgba(213,93,57,0.18)", border: "1px solid rgba(213,93,57,0.45)" }}
          >
            <Crown size={14} style={{ color: "var(--np-secondary)" }} />
            <span className="text-[11px] tracking-[0.28em] uppercase font-semibold" style={{ color: "var(--np-secondary)" }}>
              Pacchetto Premium · Solo su invito
            </span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center">
          {greetingName && (
            <p
              data-testid="coaching-greeting"
              className="text-base md:text-lg mb-3"
              style={{ color: "rgba(255,255,255,0.75)", fontFamily: "Merriweather, serif" }}
            >
              Ciao <span style={{ color: "#fff", fontWeight: 600 }}>{greetingName}</span>,
            </p>
          )}
          <h1
            className="text-3xl md:text-[46px] leading-[1.06] tracking-tight"
            style={{ fontFamily: "Merriweather, serif" }}
          >
            Il pacchetto più completo:
            <span className="block mt-2 italic" style={{ color: "var(--np-secondary)" }}>
              consulenza diretta + corso completo.
            </span>
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-[1.7]" style={{ color: "rgba(255,255,255,0.85)" }}>
            Un percorso ad alta intensità con <strong>Alessandro Fulgheri</strong>: 14 ore complessive
            di consulenza 1-to-1 dedicate alla tua situazione + accesso permanente all&apos;intero corso.
            Riservato a un numero limitato di persone all&apos;anno.
          </p>
        </div>

        {/* Items breakdown */}
        <section
          className="mt-14 rounded-3xl p-6 md:p-8"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.10)",
            backdropFilter: "blur(6px)",
          }}
        >
          <p className="text-[12px] font-semibold tracking-[0.25em] uppercase mb-5 text-center" style={{ color: "var(--np-secondary)" }}>
            Cosa è incluso
          </p>
          <ul className="space-y-4">
            {ITEMS.map(({ icon: Icon, name, desc, priceLabel }) => (
              <li
                key={name}
                className="flex items-start gap-4 p-5 rounded-2xl"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center"
                  style={{ background: "rgba(213,93,57,0.16)", border: "1px solid rgba(213,93,57,0.35)" }}
                >
                  <Icon size={18} style={{ color: "var(--np-secondary)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <p className="font-semibold text-[16px] md:text-[17px]" style={{ color: "#fff" }}>{name}</p>
                    <p
                      className="text-sm tabular-nums whitespace-nowrap"
                      style={{ color: "rgba(255,255,255,0.55)", textDecoration: "line-through", fontWeight: 500 }}
                    >
                      Valore: {priceLabel}
                    </p>
                  </div>
                  <p className="text-[14.5px] mt-1.5 leading-relaxed" style={{ color: "rgba(255,255,255,0.78)" }}>
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Total/savings strip */}
          <div className="mt-6 pt-6 border-t flex flex-col md:flex-row md:items-baseline md:justify-between gap-3" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
            <div>
              <p className="text-[12px] tracking-[0.22em] uppercase font-semibold" style={{ color: "rgba(255,255,255,0.55)" }}>
                Valore totale dei componenti
              </p>
              <p className="text-2xl mt-1 tabular-nums" style={{ fontFamily: "Merriweather, serif", color: "rgba(255,255,255,0.5)", textDecoration: "line-through" }}>
                €{SUM_FULL}
              </p>
            </div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full self-start md:self-auto"
              style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)" }}
            >
              <span className="text-[12px] font-semibold tracking-wide" style={{ color: "#86efac" }}>
                Risparmi €{SUM_FULL - SALE_PRICE}
              </span>
            </div>
          </div>
        </section>

        {/* Price + CTA */}
        <section className="mt-12">
          <div
            className="rounded-3xl p-8 md:p-10 text-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(213,93,57,0.22) 0%, rgba(213,93,57,0.06) 100%)",
              border: "1px solid rgba(213,93,57,0.40)",
            }}
          >
            <p className="text-[12px] font-semibold tracking-[0.28em] uppercase mb-2" style={{ color: "var(--np-secondary)" }}>
              Il tuo investimento oggi
            </p>
            <div className="flex items-baseline justify-center gap-3 mb-2 flex-wrap">
              <span
                className="text-2xl md:text-3xl tabular-nums"
                style={{ color: "rgba(255,255,255,0.45)", textDecoration: "line-through", fontFamily: "Merriweather, serif" }}
              >
                €{SUM_FULL}
              </span>
              <span className="text-5xl md:text-6xl font-semibold tabular-nums" style={{ fontFamily: "Merriweather, serif" }}>
                €{SALE_PRICE}
              </span>
            </div>
            <p className="text-sm mb-7" style={{ color: "rgba(255,255,255,0.7)" }}>
              Una tantum · pagamento sicuro Stripe
            </p>

            <button
              type="button"
              onClick={handleCheckout}
              disabled={submitting}
              data-testid="coaching-bundle-cta"
              className="cta-btn inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Apertura carrello…
                </>
              ) : (
                <>
                  Procedi all&apos;acquisto (€{SALE_PRICE})
                  <ArrowRight size={18} strokeWidth={2.4} />
                </>
              )}
            </button>

            {error && (
              <p className="mt-5 text-sm" data-testid="coaching-bundle-error" style={{ color: "#fca5a5" }}>
                {error}
              </p>
            )}

            <div className="mt-7 flex items-center justify-center gap-2 text-[13px]" style={{ color: "rgba(255,255,255,0.7)" }}>
              <ShieldCheck size={14} style={{ color: "var(--np-secondary)" }} />
              <span>Garanzia 60 giorni · Soddisfatto o rimborsato</span>
            </div>
          </div>
        </section>

        {/* Trust footer */}
        <p className="mt-10 text-center text-[12px]" style={{ color: "rgba(255,255,255,0.45)" }}>
          <CheckCircle2 size={11} className="inline-block mr-1.5 -mt-0.5" style={{ color: "var(--np-secondary)" }} />
          Le sessioni di consulenza vengono pianificate dopo il pagamento. Alessandro ti contatterà entro 24 ore.
        </p>
      </div>
    </main>
  );
}
